import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Clock, UserCog, Activity } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const NO_ASSIGNEE = "__none__";

function SlaCountdown({ dueAt }) {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  const mins = Math.round((due - new Date()) / 60000);
  const overdue = mins < 0;
  const txt = overdue
    ? `Overdue by ${Math.abs(Math.round(mins / 60))}h`
    : mins < 60
      ? `Due in ${mins}m`
      : `Due in ${Math.round(mins / 60)}h`;
  return (
    <span
      data-testid={overdue ? "sla-pill-overdue" : "sla-pill"}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${
        overdue
          ? "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/30"
          : "bg-[#d49a36]/15 text-[#8a5a16] border-[#d49a36]/30"
      }`}>
      <Clock className="w-3 h-3" /> {txt}
    </span>
  );
}

function ClaimTimeline({ claimId }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get(`/claims/${claimId}/timeline`)
      .then((r) => setEvents(r.data?.events || r.data?.items || r.data || []))
      .catch(() => setEvents([]));
  }, [claimId]);
  if (events === null) return <Loader2 className="w-4 h-4 animate-spin text-[#114232]" />;
  if (events.length === 0) {
    return <div className="text-xs text-[#5c6360]" data-testid={`timeline-empty-${claimId}`}>
      No timeline events recorded yet.
    </div>;
  }
  return (
    <ol className="relative border-l-2 border-[#114232]/15 pl-4 space-y-2"
        data-testid={`claim-timeline-${claimId}`}>
      {events.map((e, i) => (
        <li key={i} className="relative" data-testid={`timeline-event-${claimId}-${i}`}>
          <span className="absolute -left-[1.4rem] top-1 w-3 h-3 rounded-full bg-[#c15337]" />
          <div className="text-xs font-semibold text-[#114232] uppercase tracking-wider">
            {e.action?.replace(/_/g, " ") || e.kind || "event"}
          </div>
          <div className="text-[11px] text-[#5c6360] font-mono">
            {(e.timestamp || e.created_at || "")?.slice(0, 19).replace("T", " ")}
            {e.user_email && ` · ${e.user_email}`}
          </div>
          {e.details && (
            <pre className="text-[10px] font-mono mt-1 bg-[#114232]/5 rounded px-2 py-1 max-w-xl overflow-x-auto">
              {typeof e.details === "string" ? e.details : JSON.stringify(e.details, null, 0).slice(0, 240)}
            </pre>
          )}
        </li>
      ))}
    </ol>
  );
}

function AssignmentBlock({ claim, onChanged }) {
  const [officers, setOfficers] = useState([]);
  const [assignee, setAssignee] = useState(claim.assigned_to_user_id || NO_ASSIGNEE);
  const [slaHours, setSlaHours] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/users?role=claims_officer&limit=100")
      .then((r) => setOfficers(r.data?.items || []))
      .catch(() => setOfficers([]));
  }, []);

  async function save() {
    setSaving(true);
    try {
      const body = {
        assigned_to_user_id: assignee === NO_ASSIGNEE ? null : assignee,
      };
      if (slaHours) body.sla_hours = parseInt(slaHours, 10);
      await api.patch(`/admin/claims/${claim.id}/assign`, body);
      toast.success("Claim assignment updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-end gap-3 flex-wrap" data-testid={`assign-block-${claim.id}`}>
      <div className="min-w-[200px]">
        <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">Assigned to</Label>
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger data-testid={`assign-select-${claim.id}`} className="min-w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_ASSIGNEE}>Unassigned</SelectItem>
            {officers.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">SLA (hours)</Label>
        <input
          type="number" min={1} max={720}
          value={slaHours} onChange={(e) => setSlaHours(e.target.value)}
          placeholder="e.g. 24"
          data-testid={`assign-sla-${claim.id}`}
          className="w-24 border border-stone-300 rounded-md px-2 py-2 text-sm bg-white"
        />
      </div>
      <Button onClick={save} disabled={saving} data-testid={`assign-save-${claim.id}`}
                className="bg-[#114232] hover:bg-[#0f3a2c]">
        {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save assignment
      </Button>
      {claim.assigned_to_name && (
        <span className="text-xs text-[#5c6360]">
          Currently: <span className="font-semibold text-[#114232]">{claim.assigned_to_name}</span>
        </span>
      )}
      <SlaCountdown dueAt={claim.sla_due_at} />
    </div>
  );
}

export default function ClaimExtras({ claim, onAssignmentChanged }) {
  return (
    <div className="border-t border-[#114232]/10 bg-[#f4f2eb]/30 p-5 space-y-5" data-testid={`claim-extras-${claim.id}`}>
      <section>
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-[#c15337] mb-2">
          <UserCog className="w-3.5 h-3.5" /> Assignment & SLA
        </div>
        <AssignmentBlock claim={claim} onChanged={onAssignmentChanged} />
      </section>
      <section>
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-[#c15337] mb-2">
          <Activity className="w-3.5 h-3.5" /> Lifecycle timeline
        </div>
        <ClaimTimeline claimId={claim.id} />
      </section>
    </div>
  );
}

export { SlaCountdown };
